import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { usePollingQuery } from "../hooks/usePollingQuery";
import type {
  Driver,
  DriverDuty,
  DriverMessage,
  DriverMessageType,
  ListResponse,
  Vehicle,
  VehicleAssignment,
} from "../api/types";

const TYPES: DriverMessageType[] = ["info", "warning", "urgent", "emergency"];

function unwrap<T>(value: ListResponse<T> | T[] | undefined | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray((value as ListResponse<T>).data)) return (value as ListResponse<T>).data;
  return [];
}

function typeLabel(t: string): string {
  return t.length ? t[0].toUpperCase() + t.slice(1) : t;
}

function typeTone(t: string): "info" | "warn" | "crit" {
  if (t === "info") return "info";
  if (t === "warning") return "warn";
  return "crit"; // urgent, emergency
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export default function MessagesPage() {
  const [recipientDriverId, setRecipientDriverId] = useState("");
  const [vehicleIdOverride, setVehicleIdOverride] = useState("");
  const [messageType, setMessageType] = useState<DriverMessageType>("info");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [requireAck, setRequireAck] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [duties, setDuties] = useState<DriverDuty[]>([]);
  const [assignments, setAssignments] = useState<VehicleAssignment[]>([]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.listDrivers().then((r) => setDrivers(unwrap(r))).catch(() => setDrivers([]));
    api.listVehicles().then((r) => setVehicles(unwrap(r))).catch(() => setVehicles([]));
    api.listDriverDuties({ date: today }).then((r) => setDuties(unwrap(r))).catch(() => setDuties([]));
    api.listVehicleAssignments({ date: today }).then((r) => setAssignments(unwrap(r))).catch(() => setAssignments([]));
  }, []);

  const vehicleCodeById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const v of vehicles) m[v.id] = v.vehicle_id;
    return m;
  }, [vehicles]);

  // Real driver -> today's vehicle code, joined through block_id the same
  // way SchedulesPage's manual-override controls do — no fabricated pairing.
  const vehicleCodeByBlock = useMemo(() => {
    const m: Record<string, string> = {};
    for (const a of assignments) {
      const code = vehicleCodeById[a.vehicle_id];
      if (code) m[a.block_id] = code;
    }
    return m;
  }, [assignments, vehicleCodeById]);

  const vehicleCodeByDriver = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of duties) {
      if (d.block_id && vehicleCodeByBlock[d.block_id]) m[d.driver_id] = vehicleCodeByBlock[d.block_id];
    }
    return m;
  }, [duties, vehicleCodeByBlock]);

  const driverById = useMemo(() => {
    const m: Record<string, Driver> = {};
    for (const d of drivers) m[d.id] = d;
    return m;
  }, [drivers]);

  function driverLabel(driverId: string): string {
    const d = driverById[driverId];
    const name = d ? `${d.first_name} ${d.last_name}`.trim() : `driver ${driverId.slice(0, 8)}…`;
    const vc = vehicleCodeByDriver[driverId];
    return vc ? `${name} · ${vc}` : name;
  }

  const messagesQuery = usePollingQuery<DriverMessage[]>(
    () => api.listAgencyMessages({ limit: 100 }),
    [],
    { intervalMs: 8000 }
  );
  const messages = messagesQuery.data ?? [];

  const [ackFilter, setAckFilter] = useState<"all" | "needs">("all");
  const filteredMessages = useMemo(
    () =>
      ackFilter === "needs"
        ? messages.filter((m) => m.requires_acknowledgment && !m.acknowledged_at)
        : messages,
    [messages, ackFilter]
  );
  const todayCount = useMemo(() => messages.filter((m) => isToday(m.created_at)).length, [messages]);

  function toLabel(m: DriverMessage): string {
    if (m.recipient_driver_id) return driverLabel(m.recipient_driver_id);
    if (m.recipient_vehicle_id) {
      return `Vehicle ${vehicleCodeById[m.recipient_vehicle_id] ?? m.recipient_vehicle_id.slice(0, 8) + "…"}`;
    }
    return "All drivers";
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!body.trim()) {
      setError("Message body is required");
      return;
    }
    setPending(true);
    try {
      await api.sendMessage({
        recipient_driver_id: vehicleIdOverride.trim() ? undefined : recipientDriverId || undefined,
        recipient_vehicle_id: vehicleIdOverride.trim() || undefined,
        message_type: messageType,
        subject: subject.trim() || undefined,
        body: body.trim(),
        requires_acknowledgment: requireAck,
      });
      setBody("");
      setSubject("");
      await messagesQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="page msg-page">
      <div className="msg-header">
        <div>
          <h1>Dispatch Messaging</h1>
          <p className="secondary-text">Send instructions to drivers and track acknowledgments.</p>
        </div>
      </div>

      <div className="msg-hero-grid">
        <section className="card msg-compose-card">
          <h3>New message</h3>
          <form onSubmit={onSubmit}>
            <label className="msg-field">
              <span>To</span>
              <select value={recipientDriverId} onChange={(e) => setRecipientDriverId(e.target.value)}>
                <option value="">All drivers</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{driverLabel(d.id)}</option>
                ))}
              </select>
            </label>

            <div className="msg-form-row">
              <label className="msg-field">
                <span>Type</span>
                <select value={messageType} onChange={(e) => setMessageType(e.target.value as DriverMessageType)}>
                  {TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                </select>
              </label>
              <label className="msg-field">
                <span>Subject</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="optional" />
              </label>
            </div>

            <label className="msg-field">
              <span>Message</span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                placeholder="Type your message to the driver..."
                required
              />
            </label>

            <details className="msg-advanced">
              <summary>Advanced</summary>
              <label className="msg-field" style={{ marginTop: 8 }}>
                <span>Recipient vehicle ID (optional, overrides driver)</span>
                <input
                  value={vehicleIdOverride}
                  onChange={(e) => setVehicleIdOverride(e.target.value)}
                  placeholder="UUID"
                />
              </label>
            </details>

            <button
              type="button"
              className={`msg-toggle-row ${requireAck ? "on" : ""}`}
              onClick={() => setRequireAck((v) => !v)}
              aria-pressed={requireAck}
            >
              <span className="msg-toggle" role="switch" aria-checked={requireAck} />
              Require acknowledgment
            </button>

            {error && <div className="banner banner-error">{error}</div>}

            <div className="msg-actions-row">
              <button className="btn primary msg-send-btn" type="submit" disabled={pending}>
                {pending ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        </section>

        <section className="card msg-log-card">
          <div className="msg-log-head">
            <h3><span className="msg-log-bar" />Agency messages <span className="msg-log-count secondary-text">{todayCount} today</span></h3>
            <div className="msg-segmented">
              <button type="button" className={ackFilter === "all" ? "active" : ""} onClick={() => setAckFilter("all")}>All</button>
              <button type="button" className={ackFilter === "needs" ? "active" : ""} onClick={() => setAckFilter("needs")}>Needs ack</button>
            </div>
          </div>
          <div className="msg-table-wrap">
            <table className="msg-table">
              <thead>
                <tr><th>When</th><th>Type</th><th>To</th><th>Message</th><th>Ack</th></tr>
              </thead>
              <tbody>
                {messagesQuery.loading && messages.length === 0 && (
                  <tr><td colSpan={5} className="empty">Loading…</td></tr>
                )}
                {!messagesQuery.loading && filteredMessages.length === 0 && (
                  <tr><td colSpan={5} className="empty">— no messages —</td></tr>
                )}
                {filteredMessages.map((m) => (
                  <tr key={m.id}>
                    <td className="msg-when">{fmtTime(m.created_at)}</td>
                    <td><span className={`msg-type-pill ${typeTone(m.message_type)}`}>{typeLabel(m.message_type)}</span></td>
                    <td className="msg-to">{toLabel(m)}</td>
                    <td className="msg-body-cell">
                      {m.subject && <strong>{m.subject} · </strong>}
                      {m.body}
                    </td>
                    <td>
                      {!m.requires_acknowledgment ? (
                        <span className="msg-ack-none">—</span>
                      ) : m.acknowledged_at ? (
                        <span className="msg-ack-yes">ACKED {fmtTime(m.acknowledged_at)}</span>
                      ) : (
                        <span className="msg-ack-pending">PENDING</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
